import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress, Alert, Snackbar } from '@mui/material';
import NavbarStaff from '../../components/NavbarStaff';
import Footer from '../../components/Footer';
import { useAuthStaff } from '../../hooks/useAuthStaff';
import CalculateMoneyViewStaff from './CalculateMoneyStaff/CalculateMoneyViewStaff';
import { useWorkSessionsContext } from '../../context/WorkSessionsContext';
import { useCalculateMoneyContext } from '../../context/CalculateMoneyContext';
import { useStaffsContext } from '../../context/StaffsContext';
import { useServicesContext } from '../../context/ServicesContext';
import { markStaffWorkSessionNotificationAsSeen } from '../../lib/apiClientStaff';

import * as signalR from '@microsoft/signalr';
import {
  RootContainer,
  MainContainer,
  ContentContainer,
  StyledCard,
  LoadingContainer,
} from '../../styles/OwnerStyle/OwnerDashboardStyles';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

function getISOWeek(date) {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  return 1 + Math.round((diff / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
}

const StaffDashboard = () => {
  const { user } = useAuthStaff();
  const staffId = user?.staffId ?? localStorage.getItem('staffUserId');
  const businessId = user?.businessId ?? localStorage.getItem('selectedBusinessId');

  const {
    staff,
    fetchAllStaff,
    staffNotifications,
    fetchStaffWorkSessionNotifications,
  } = useStaffsContext();
  const { workSessions, fetchWorkSessionsByStaffData } = useWorkSessionsContext();

  const { services, categories, fetchServices, fetchCategories } = useServicesContext();

  const {
    staffEarnings,
    staffTodaySummary,
    getStaffYearlyEarnings,
    getStaffMonthlyEarnings,
    getStaffWeeklyEarnings,
    getStaffDailyEarnings,
    getStaffTodaySummary,
  } = useCalculateMoneyContext();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const connectionRef = useRef(null);

  // Initial load
  useEffect(() => {
    if (!businessId || !staffId) return;
    setLoading(true);
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentWeek = getISOWeek(now);
    const todayDate = now.toISOString().slice(0, 10);

    Promise.all([
      fetchAllStaff(businessId),
      fetchWorkSessionsByStaffData(staffId),
      fetchServices(businessId),
      fetchCategories(),
      getStaffYearlyEarnings(businessId, staffId, 5),
      getStaffMonthlyEarnings(businessId, staffId, currentYear),
      getStaffWeeklyEarnings(businessId, staffId, currentYear, currentWeek),
      getStaffDailyEarnings(businessId, staffId, todayDate),
      getStaffTodaySummary(businessId, staffId),
      // ✅ fetch notifications initially so the bell is populated
      fetchStaffWorkSessionNotifications(staffId),
    ])
      .catch((err) => setError(err?.message || 'Error fetching staff data'))
      .finally(() => setLoading(false));
  }, [
    businessId,
    staffId,
    fetchAllStaff,
    fetchWorkSessionsByStaffData,
    fetchServices,
    fetchCategories,
    getStaffYearlyEarnings,
    getStaffMonthlyEarnings,
    getStaffWeeklyEarnings,
    getStaffDailyEarnings,
    getStaffTodaySummary,
    fetchStaffWorkSessionNotifications, // ✅ include dependency
  ]);

  // Real-time updates
  useEffect(() => {
    if (!businessId || !staffId) return;

    const connectToHub = async () => {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${apiBaseUrl}/appointmentHub`)
        .withAutomaticReconnect()
        .build();
      try {
        await connection.start();
        connectionRef.current = connection;

        ['ReceiveWorkSessionCreated', 'ReceiveWorkSessionUpdated', 'ReceiveWorkSessionDeleted'].forEach((event) => {
          connection.on(event, async () => {
            const now = new Date();
            const year = now.getFullYear();
            const week = getISOWeek(now);
            const todayDate = now.toISOString().slice(0, 10);

            await fetchWorkSessionsByStaffData(staffId);
            await fetchStaffWorkSessionNotifications(staffId);
            await Promise.all([
              getStaffYearlyEarnings(businessId, staffId, 5),
              getStaffMonthlyEarnings(businessId, staffId, year),
              getStaffWeeklyEarnings(businessId, staffId, year, week),
              getStaffDailyEarnings(businessId, staffId, todayDate),
              getStaffTodaySummary(businessId, staffId),
            ]);
          });
        });
      } catch (error) {
        setError('Error connecting to real-time updates');
      }
    };

    connectToHub();
    return () => {
      if (connectionRef.current) connectionRef.current.stop();
    };
  }, [
    businessId,
    staffId,
    fetchWorkSessionsByStaffData,
    fetchStaffWorkSessionNotifications, // ✅ include dependency
    getStaffYearlyEarnings,
    getStaffMonthlyEarnings,
    getStaffWeeklyEarnings,
    getStaffDailyEarnings,
    getStaffTodaySummary,
  ]);

  async function handleMarkSeen(unseenIds) {
    await Promise.all(unseenIds.map((id) => markStaffWorkSessionNotificationAsSeen(staffId, id)));
    await fetchStaffWorkSessionNotifications(staffId); // refresh
  }

  return (
    <RootContainer>
      <NavbarStaff />
      <MainContainer>
        <ContentContainer>
        
          <StyledCard>
            {loading ? (
              <LoadingContainer>
                <CircularProgress />
              </LoadingContainer>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <CalculateMoneyViewStaff
                businessId={businessId}
                staff={staff}
                workSessions={workSessions}
                earningsSummary={staffEarnings}
                staffId={staffId}
                staffTodaySummary={staffTodaySummary}
                currentWeek={getISOWeek(new Date())}
                services={services}
                categories={categories}
                onShowSuccess={(msg) => setSuccessMessage(msg)}
                notifications={staffNotifications} 
                onMarkSeen={handleMarkSeen}
              />
            )}
          </StyledCard>
        </ContentContainer>
      </MainContainer>
      <Footer />
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: '70px' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </RootContainer>
  );
};

export default StaffDashboard;
